import { useMemo, useState } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';
import { useTheme } from '../../../context/ThemeContext';
import { useTaskContext } from '../../../context/TaskContext';
import { useDashboardActivity } from '../../../hooks/use-api-dashboard';
import { countNewActivity } from '@/lib/adapters/activity';

interface IconSidebarProps {
  activeSection: string;
  setActiveSection: (section: string) => void;
  triageCount?: number;
  announcementsCount?: number;
}

const LAST_VISIT_KEY = 'activity_last_visited';

/** Epoch ms of the last visit to Activity, or null when unknown or storage is unavailable. */
function readLastVisit(): number | null {
  try {
    const stored = localStorage.getItem(LAST_VISIT_KEY);
    if (!stored) return null;
    const parsed = parseInt(stored, 10);
    return Number.isNaN(parsed) ? null : parsed;
  } catch {
    return null;
  }
}

function writeLastVisit(timestamp: number): void {
  try {
    localStorage.setItem(LAST_VISIT_KEY, timestamp.toString());
  } catch {
    // Storage can be blocked (private mode, cleared site data). The in-memory visit below still clears the badge.
  }
}

const navSections = [
  {
    label: 'Overview',
    items: [
      { id: 'overview', icon: 'ri-dashboard-line', label: 'Dashboard' },
      { id: 'triage', icon: 'ri-inbox-unarchive-line', label: 'Triage' },
    ]
  },
  {
    label: 'Clients',
    items: [
      { id: 'client-portal', icon: 'ri-user-star-line', label: 'Client Portal' },
      { id: 'client-health', icon: 'ri-heart-pulse-line', label: 'Client Health' },
    ]
  },
  {
    label: 'Work',
    items: [
      { id: 'tasks', icon: 'ri-task-line', label: 'Tasks' },
      { id: 'recurring', icon: 'ri-repeat-line', label: 'Recurring Tasks' },
      { id: 'workflows', icon: 'ri-flow-chart', label: 'Workflows' },
      { id: 'calendar', icon: 'ri-calendar-line', label: 'Calendar' },
      { id: 'kanban', icon: 'ri-layout-grid-line', label: 'Kanban' },
      { id: 'task-dependencies', icon: 'ri-node-tree', label: 'Dependencies' },
    ]
  },
  {
    label: 'Finance',
    items: [
      { id: 'xero', icon: 'ri-file-list-3-line', label: 'Xero' },
      { id: 'time-tracking', icon: 'ri-time-line', label: 'Time Tracking' },
      { id: 'compliance', icon: 'ri-shield-check-line', label: 'Compliance' },
      { id: 'reports', icon: 'ri-file-chart-line', label: 'Reports' },
    ]
  },
  {
    label: 'Resources',
    items: [
      { id: 'documents', icon: 'ri-folder-line', label: 'Documents' },
      { id: 'notes', icon: 'ri-sticky-note-line', label: 'Quick Notes' },
      { id: 'communications', icon: 'ri-message-3-line', label: 'Messages' },
    ]
  },
  {
    label: 'Team',
    items: [
      { id: 'staff-workload', icon: 'ri-team-line', label: 'Staff Workload' },
      { id: 'staff-onboarding', icon: 'ri-user-add-line', label: 'Staff Onboarding' },
      { id: 'announcements', icon: 'ri-notification-line', label: 'Announcements' },
      { id: 'activity', icon: 'ri-pulse-line', label: 'Activity' },
    ]
  },
  {
    label: 'System',
    items: [
      { id: 'analytics', icon: 'ri-bar-chart-box-line', label: 'Analytics' },
      { id: 'integrations', icon: 'ri-plug-line', label: 'Integrations' },
    ]
  },
];

const bottomItems = [
  { id: 'settings', icon: 'ri-settings-3-line', label: 'Settings' },
];

export default function IconSidebar({ activeSection, setActiveSection, triageCount = 0, announcementsCount = 0 }: IconSidebarProps) {
  const [expanded, setExpanded] = useState(false);
  const { isDark } = useTheme();
  const { taskCount } = useTaskContext();
  const [searchParams] = useSearchParams();
  const search = searchParams.toString() ? `?${searchParams.toString()}` : '';
  const [seenTaskCount, setSeenTaskCount] = useState<number | null>(null);

  const activityQuery = useDashboardActivity(50);
  const [lastVisit, setLastVisit] = useState<number | null>(readLastVisit);
  // Counted as of the last fetch, so the badge does not read the clock during render.
  const newActivityCount = useMemo(
    () => countNewActivity(activityQuery.data?.data ?? [], lastVisit, activityQuery.dataUpdatedAt),
    [activityQuery.data, activityQuery.dataUpdatedAt, lastVisit],
  );

  // Update seen task count when viewing tasks
  const [prevActiveSection, setPrevActiveSection] = useState(activeSection);
  const [prevTaskCount, setPrevTaskCount] = useState(taskCount);
  if (prevActiveSection !== activeSection || prevTaskCount !== taskCount) {
    setPrevActiveSection(activeSection);
    setPrevTaskCount(taskCount);
    // First real count becomes the baseline; later increases show as new until Tasks is opened.
    if (taskCount !== null && (activeSection === 'tasks' || seenTaskCount === null)) {
      setSeenTaskCount(taskCount);
    }
  }

  const newTaskCount = taskCount !== null && seenTaskCount !== null ? taskCount - seenTaskCount : 0;

  const handleSetSection = (id: string) => {
    setActiveSection(id);
    if (id === 'activity') {
      const now = new Date().getTime();
      writeLastVisit(now);
      setLastVisit(now);
    }
    if (id === 'tasks' && taskCount !== null) {
      setSeenTaskCount(taskCount);
    }
  };

  const sidebarBg = isDark ? 'bg-[#0D0F14] border-white/[0.12]' : 'bg-white border-gray-200/80';
  const divider = isDark ? 'bg-white/[0.06]' : 'bg-gray-100';
  const activeItem = isDark
    ? 'bg-teal-500/15 text-teal-300 border-teal-500/25'
    : 'bg-teal-50 text-teal-700 border-teal-200';
  const inactiveItem = isDark
    ? 'text-gray-500 hover:bg-white/5 hover:text-gray-300 border-transparent'
    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700 border-transparent';
  const activeLine = isDark ? 'bg-teal-400 shadow-teal-400/60' : 'bg-teal-500';
  const logoText = isDark ? 'text-white' : 'text-gray-900';
  const sectionLabel = isDark ? 'text-gray-500' : 'text-gray-500';

  const NavItem = ({ item, onClick }: { item: { id: string; icon: string; label: string }; onClick: () => void }) => {
    const isActivity = item.id === 'activity';
    const isTask = item.id === 'tasks';
    const isTriage = item.id === 'triage';
    const isAnnouncements = item.id === 'announcements';
    const showActivityBadge = isActivity && newActivityCount > 0;
    const showTaskBadge = isTask && newTaskCount > 0;
    const showTriageBadge = isTriage && triageCount > 0;
    const showAnnouncementsBadge = isAnnouncements && announcementsCount > 0;
    const showBadge = showActivityBadge || showTaskBadge || showTriageBadge || showAnnouncementsBadge;
    const badgeCount = isTask ? newTaskCount : isTriage ? triageCount : isAnnouncements ? announcementsCount : newActivityCount;
    const to = item.id === 'overview' ? `/${search}` : `/${item.id}${search}`;

    return (
      <NavLink
        to={to}
        onClick={onClick}
        end={item.id === 'overview'}
        data-tour={item.id === 'tasks' ? 'tasks' : item.id === 'client-portal' ? 'clients' : undefined}
        className={({ isActive }) =>
          `w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 whitespace-nowrap overflow-hidden relative group ${
            isActive
              ? `${activeItem} border`
              : `${inactiveItem} border`
          }`
        }
      >
        {({ isActive }) => (
          <>
            {isActive && (
              <span className={`absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 ${activeLine} rounded-r-full shadow-sm`}></span>
            )}
            <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 relative">
              <i className={`${item.icon} text-base leading-none`}></i>
              {showBadge && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-0.5 flex items-center justify-center rounded-full text-[9px] font-bold leading-none bg-rose-500 text-white shadow-sm shadow-rose-500/50 animate-pulse">
                  {badgeCount > 99 ? '99+' : badgeCount}
                </span>
              )}
            </span>
            <span className={`text-xs font-medium transition-all duration-200 ${expanded ? 'opacity-100 w-auto' : 'opacity-0 w-0'}`}>
              {item.label}
            </span>
            {showBadge && expanded && (
              <span className="ml-auto flex-shrink-0 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full text-[9px] font-bold bg-rose-500 text-white shadow-sm shadow-rose-500/40">
                {badgeCount > 99 ? '99+' : badgeCount}
              </span>
            )}
          </>
        )}
      </NavLink>
    );
  };

  return (
    <aside
      data-tour="sidebar"
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
      className={`${sidebarBg} border-r hidden md:flex flex-col py-4 gap-1 flex-shrink-0 z-20 transition-all duration-300 ease-in-out overflow-hidden ${expanded ? 'w-52' : 'w-14'}`}
    >
      {/* Logo area */}
      <div className="flex items-center gap-3 px-3 mb-3 overflow-hidden">
        <span className="w-8 h-8 flex items-center justify-center bg-gradient-to-br from-teal-400 to-teal-600 rounded-lg flex-shrink-0 shadow-md shadow-teal-500/30">
          <i className="ri-book-2-line text-white text-sm"></i>
        </span>
        <span className={`text-sm font-bold ${logoText} whitespace-nowrap tracking-wide transition-all duration-200 ${expanded ? 'opacity-100 w-auto' : 'opacity-0 w-0'}`}>
          BookKeep
        </span>
      </div>

      <div className={`w-full h-px ${divider} mb-2`} />

      {/* Nav sections */}
      <div className="flex-1 flex flex-col gap-1 px-2 overflow-y-auto">
        {navSections.map((section, sectionIdx) => (
          <div key={sectionIdx} className="mb-2">
            {expanded && (
              <div className={`px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider ${sectionLabel} transition-all duration-200`}>
                {section.label}
              </div>
            )}
            <div className="flex flex-col gap-0.5">
              {section.items.map((item) => (
                <NavItem key={item.id} item={item} onClick={() => handleSetSection(item.id)} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Bottom nav items */}
      <div className="flex flex-col gap-0.5 px-2 pt-2 border-t border-opacity-50" style={{ borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}>
        {bottomItems.map((item) => (
          <NavItem key={item.id} item={item} onClick={() => handleSetSection(item.id)} />
        ))}
      </div>
    </aside>
  );
}